import { appConfig } from "@shared/utils/appConfig";
import { useEffect, useSyncExternalStore } from "react";
import { useFeatureFlagEnabled, useFeatureFlagPayload } from "posthog-js/react";

const POSTHOG_PROJECT_TOKEN = appConfig.POSTHOG_API_KEY;

const knownPostHogFeatureFlagsKeys = [
  "crm-v1",
  "radar-contacts-csv-import-limit",
  "radar-event-subscriptions",
  "voice-input-message-bar",
] as const;

type KnownFlagKeys = (typeof knownPostHogFeatureFlagsKeys)[number];
type FlagType = {
  enabled: boolean;
  value: number | boolean | string | null;
};

type MockFlags = Record<KnownFlagKeys, FlagType>;

const fallbackFlagValues: MockFlags = {
  "crm-v1": { enabled: false, value: null },
  "radar-contacts-csv-import-limit": { enabled: false, value: null },
  "radar-event-subscriptions": { enabled: false, value: null },
  "voice-input-message-bar": { enabled: false, value: null },
};

const useLiveFeatureFlag = (flag: KnownFlagKeys): boolean | null => {
  const isEnabled = useFeatureFlagEnabled(flag);

  if (!knownPostHogFeatureFlagsKeys.includes(flag)) {
    console.warn(`Feature flag "${flag}" is not known in posthogFeatureFlags.`);
    return false;
  }

  if (isEnabled === undefined) return null;

  return !!isEnabled;
};

const useLiveFeatureFlagValue = (flag: KnownFlagKeys): FlagType["value"] => {
  const payload = useFeatureFlagPayload(flag);

  if (!knownPostHogFeatureFlagsKeys.includes(flag)) {
    console.warn(`Feature flag "${flag}" is not known in posthogFeatureFlags.`);
    return null;
  }

  if (
    typeof payload === "number" ||
    typeof payload === "string" ||
    typeof payload === "boolean"
  ) {
    return payload;
  }

  return null;
};

/**
 * This is a mock implementation of the useFeatureFlag hook. It fetches the flag values from a local JSON file and updates the state accordingly. This allows us to test the feature flags without relying on the actual PostHog service.
 */
type MockState = { flags: MockFlags; loaded: boolean };

let mockState: MockState = { flags: fallbackFlagValues, loaded: false };
let mockFetchPromise: Promise<void> | null = null;
const mockListeners = new Set<() => void>();

const subscribeMockFlags = (listener: () => void) => {
  mockListeners.add(listener);
  return () => mockListeners.delete(listener);
};

const useMockFlagsState = (): MockState => {
  const state = useSyncExternalStore(subscribeMockFlags, () => mockState);

  useEffect(() => {
    if (mockFetchPromise) return;
    mockFetchPromise = fetch("/posthog.mock.json")
      .then((r) => r.json())
      .then((data: Partial<MockFlags>) => {
        mockState = { flags: { ...fallbackFlagValues, ...data }, loaded: true };
        mockListeners.forEach((l) => l());
      })
      .catch(() => {
        mockFetchPromise = null;
        mockState = { flags: fallbackFlagValues, loaded: true };
        mockListeners.forEach((l) => l());
      });
  }, []);

  return state;
};

const useMockFeatureFlag = (flag: KnownFlagKeys): boolean | null => {
  const { flags, loaded } = useMockFlagsState();

  if (!loaded) return null;
  return flags[flag].enabled;
};

const useMockFeatureFlagValue = (flag: KnownFlagKeys): FlagType["value"] => {
  const { flags, loaded } = useMockFlagsState();

  if (!loaded) return null;
  return flags[flag].enabled ? flags[flag].value : null;
};

export const useFeatureFlag = !POSTHOG_PROJECT_TOKEN
  ? useMockFeatureFlag
  : useLiveFeatureFlag;

export const useFeatureFlagValue = !POSTHOG_PROJECT_TOKEN
  ? useMockFeatureFlagValue
  : useLiveFeatureFlagValue;
